<template>
  <div v-if="isOrderLoading">
    <DrawerHeader @close="emit('cancel')" />
    <QueryLoading />
  </div>
  <div
    v-else-if="
      isOrderError || (order != null && !orderWithInvoice)
    "
  >
    <DrawerHeader @close="emit('cancel')" />

    <QueryError> Failed to retrieve order </QueryError>
  </div>

  <div
    v-else-if="orderWithInvoice && step === 'summary'"
    class="flex h-full flex-col"
  >
    <DrawerHeader
      :hide-back="totalPaid !== 0"
      @close="emit('cancel')"
    >
      Split Payment (#{{ orderWithInvoice.orderNumber }})
    </DrawerHeader>
    <div class="flex h-full flex-col overflow-hidden px-6">
      <!-- Transactions -->
      <div class="flex-1 overflow-y-auto">
        <div class="divide-y divide-background-fg/5">
          <template
            v-for="payment in orderWithInvoice.payments"
            :key="payment.id"
          >
            <TransactionItem
              v-if="payment.status !== 'cancelled'"
              :transaction="payment"
              @click-transaction="onClickTransaction"
            />
          </template>
        </div>
        <div class="mt-3 text-center text-background-fg/40">
          {{ postPaymentsText }}
        </div>
      </div>
    </div>

    <!-- Summary & Actions -->
    <div
      class="flex flex-col space-y-3 border-t border-background-fg/5 p-4"
    >
      <div>
        <StatRow
          label="Total Cost"
          :value="orderWithInvoice.invoice.amountGross"
          type="currency"
        />
        <StatRow
          :label="totalPaidLabel"
          :value="totalPaidGross"
          type="currency"
        />
        <StatRow
          v-if="totalRemaining"
          label="Total Remaining"
          :value="totalRemaining"
          type="currency"
        />
        <StatRow
          v-if="totalGratuityPaid"
          label="Total Gratuity"
          :value="totalGratuityPaid"
          type="currency"
        />
        <StatRow
          v-if="totalCashbackPaid"
          label="TOTAL CASHBACK"
          :value="totalCashbackPaid"
          type="currency"
        />
      </div>
      <ActionButtonSecondary
        :disabled="
          (splitConfiguration === 'manual' && !totalPaid) ||
          (splitConfiguration !== 'manual' &&
            (order?.payments.length ?? 0) <
              splitConfiguration)
        "
        @click="onClickCompletePayment"
      >
        Complete payment
      </ActionButtonSecondary>
      <ActionButtonSecondary
        :disabled="
          !isCashEnabled ||
          (splitConfiguration !== 'manual' &&
            !totalRemaining)
        "
        @click="step = 'cash'"
      >
        {{ cashPaymentText }}
      </ActionButtonSecondary>
      <ActionButtonPrimary
        :disabled="
          splitConfiguration !== 'manual' && !totalRemaining
        "
        @click="step = 'card'"
      >
        <IconCard class="mr-2" />
        {{ cardPaymentText }}
      </ActionButtonPrimary>
    </div>
  </div>

  <SplitPaymentFixedCashEntry
    v-else-if="step === 'cash' && equalSplitAmount"
    :amount-due="equalSplitAmount"
    @submit="onConfirmCash"
    @back="step = 'summary'"
  />
  <SplitPaymentManualCashEntry
    v-else-if="step === 'cash'"
    :remaining-due="totalRemaining"
    @submit="onConfirmCash"
    @back="step = 'summary'"
  />

  <SplitPaymentCardEntry
    v-else-if="step === 'card'"
    :remaining-due="totalRemaining"
    :amount="equalSplitAmount"
    @submit="onConfirmCard"
    @back="step = 'summary'"
  />

  <SplitPaymentInsufficientAmountConfirmation
    ref="insufficientAmountConfirmationRef"
  />

  <TransactionDrawer
    v-if="currentTransaction"
    :is-open="isTransactionDrawerOpen"
    show-retry
    :transaction="currentTransaction"
    @close="handleCloseTransactionDrawer"
  />
  <SignatureConfirmation ref="signatureConfirmationRef" />

  <PaymentRetryDrawer ref="paymentRetryDrawerRef" />
</template>

<script setup lang="ts">
import { createTransaction } from "@/services/createTransaction"
import {
  type RetrieveOrderResponse,
  type OrderDetailInvoice,
  useOrder,
  type OrderDetailPayment,
  type OrderDetailRefund,
} from "@/services/orders/retrieveOrder"

import { forcePayInvoice } from "@/services/invoices/forcePayInvoice"

import * as Sentry from "@sentry/vue"
import { IPayment } from "@/androidPlugins/IPayment"

import { retrieveTransactionReceipt } from "@/services/transactions/retrieveTransactionReceipt"
import { IPrinter } from "@/androidPlugins/IPrinter"
import type {
  CreateTransactionResponse,
  CreateTransactionResponseInvoice,
} from "@/services/createTransaction"

import type SignatureConfirmation from "./SignatureConfirmation.vue"
import type PaymentRetryDrawer from "./PaymentRetryDrawer.vue"

const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  orderId: string
  splitConfiguration: number | "manual"
}>()

const emit = defineEmits<{
  (event: "error"): void
  (
    event: "success",
    invoice: CreateTransactionResponseInvoice
  ): void
  (event: "cancel"): void
}>()

const { printingSupported } = useDevice()

const {
  isCashEnabled,
  isCardReceiptsEnabled,
  gratuityIsAvailable,
  tenant,
} = useTenant()
const {
  data: order,
  isError: isOrderError,
  isLoading: isOrderLoading,
} = useOrder(props.orderId)
const { startLoading, stopLoading } =
  usePageBlockingLoading()

whenever(
  () =>
    order.value?.invoice?.isPaid &&
    !isTransactionDrawerOpen.value,
  () => {
    order.value?.invoice &&
      emit("success", order.value.invoice)
  }
)

const step = ref<"summary" | "cash" | "card">("summary")
const insufficientAmountConfirmationRef = useTemplateRef(
  "insufficientAmountConfirmationRef"
)
const signatureConfirmationRef = useTemplateRef(
  "signatureConfirmationRef"
)
type OrderWithInvoice = RetrieveOrderResponse & {
  invoice: OrderDetailInvoice
}
const orderWithInvoice = computed<OrderWithInvoice | null>(
  () => {
    if (order.value === undefined) {
      return null
    }

    if (order.value !== undefined && order.value.invoice) {
      return order.value as OrderWithInvoice
    }

    toast.error("Something went wrong")
    emit("error")
    return null
  }
)

const totalPaid = computed(() => {
  if (orderWithInvoice.value === null) {
    return 0
  }

  return orderWithInvoice.value.invoice.amountBalancedTotal
})

const totalPaidGross = computed(() => {
  if (orderWithInvoice.value === null) {
    return 0
  }

  return orderWithInvoice.value.invoice.amountBalancedGross
})

const totalPaidLabel = computed(() =>
  props.splitConfiguration === "manual"
    ? "Total Paid"
    : `Total Paid (${order.value?.payments.length ?? 0}/${
        props.splitConfiguration
      })`
)

const postPaymentsText = computed(() => {
  const paymentCount =
    order.value?.payments.filter(
      (p) => p.status === "successful"
    ).length ?? 0

  if (typeof props.splitConfiguration === "number") {
    const refundsExist = order.value?.payments.some(
      (p) => p.refunds.length
    )

    if (refundsExist) {
      // Once a refund exists at least one extra payment will be required, better to just leave this text blank
      return null
    }

    const paymentsRemaining =
      props.splitConfiguration - paymentCount

    return paymentsRemaining === 1
      ? "1 payment remaining"
      : `${paymentsRemaining} payments remaining`
  }

  return paymentCount === 0 ? "No payments made" : null
})

const totalGratuityPaid = computed(() => {
  if (!orderWithInvoice.value) {
    return 0
  }

  return orderWithInvoice.value.invoice
    .amountBalancedGratuity
})

const totalCashbackPaid = computed(() => {
  if (!orderWithInvoice.value) {
    return 0
  }

  return orderWithInvoice.value.invoice
    .amountBalancedCashback
})

const totalRemaining = computed(() => {
  if (orderWithInvoice.value == null) {
    return 0
  }

  return Math.max(
    orderWithInvoice.value.invoice.amountGross -
      totalPaidGross.value,
    0
  )
})

const equalSplitAmount = computed<number | null>(() => {
  if (
    orderWithInvoice.value === null ||
    props.splitConfiguration === "manual"
  ) {
    return null
  }

  return Math.min(
    Math.ceil(
      orderWithInvoice.value.invoice.amountGross /
        props.splitConfiguration
    ),
    totalRemaining.value
  )
})

const cashPaymentText = computed(() =>
  equalSplitAmount.value
    ? `Cash payment- ${formatToMerchantCurrencyString(
        equalSplitAmount.value
      )}`
    : "Cash payment"
)

const cardPaymentText = computed(() =>
  equalSplitAmount.value
    ? `Card payment- ${formatToMerchantCurrencyString(
        equalSplitAmount.value
      )}`
    : "Card payment"
)

const currentTransaction = ref<
  | CreateTransactionResponse
  | OrderDetailPayment
  | OrderDetailRefund
>()
const isTransactionDrawerOpen = ref(false)
const showTransactionDrawer = () => {
  isTransactionDrawerOpen.value = true
}
const handleCloseTransactionDrawer = () => {
  isTransactionDrawerOpen.value = false
}

const onConfirmCard = async (
  amount: number,
  gratuity: number
) => {
  if (!signatureConfirmationRef.value) return

  if (orderWithInvoice.value == null) return
  const devicePaymentData = await makeDeviceCardPayment(
    amount,
    gratuity
  )

  if (devicePaymentData === null) {
    step.value = "summary"
    return
  }

  startLoading()
  try {
    const transaction = await syncTransaction(
      orderWithInvoice.value.invoice.id,
      {
        data: devicePaymentData,
        source: "pos_card_split",
        skipInvoicePaidCheck: true,
      }
    )

    let forceSkipReceiptDrawer = false
    const showSignatureConfirmation =
      "signatureStatus" in transaction &&
      transaction.signatureStatus === "AWAITING APPROVAL"

    if (showSignatureConfirmation) {
      await maybePrintMerchantReceipt(transaction, true) // Contains signature line

      const { isCanceled: isSignatureRejected } =
        await signatureConfirmationRef.value.reveal({
          transactionId: transaction.id,
          transactionUti: transaction.uti ?? "",
          transactionAmount:
            transaction.amountTransactedTotal,
        })

      forceSkipReceiptDrawer = isSignatureRejected // In this case transaction is voided, but transaction objects passed to receipt drawer shows as successful
    }

    await onPaymentSuccess(
      transaction,
      forceSkipReceiptDrawer,
      showSignatureConfirmation
    )
  } catch {
    toast.error("Failed to create card transaction")
  } finally {
    stopLoading()
    step.value = "summary"
  }
}

const makeDeviceCardPayment = async (
  amountTrans: number,
  amountGratuity: number
) => {
  startLoading()
  try {
    const devicePaymentResponse =
      await IPayment.makeDevicePayment({
        amountTrans,
        amountGratuity,
        disablePrinting:
          !isCardReceiptsEnabled.value ||
          !printingSupported.value,
        cardholderNotPresent: false,
        gratuityIsAvailable: gratuityIsAvailable.value,
      })

    return devicePaymentResponse.data
  } catch (error) {
    toast.error("Payment application failed")
    Sentry.captureException(error)
    return null
  } finally {
    stopLoading()
  }
}

const onConfirmCash = async (
  cashTendered: number,
  changeGiven: number,
  amountDue?: number
) => {
  if (orderWithInvoice.value == null) return

  startLoading()
  try {
    const transaction = await syncTransaction(
      orderWithInvoice.value.invoice.id,
      {
        source: "pos_cash_split",
        data: {
          cashTendered,
          changeGiven,
          amountDue,
        },
        skipInvoicePaidCheck: true,
      }
    )
    await onPaymentSuccess(transaction)
  } catch {
    toast.error("Something went wrong")
  } finally {
    stopLoading()
    step.value = "summary"
  }
}

const onPaymentSuccess = async (
  transaction: CreateTransactionResponse,
  forceSkipReceiptDrawer?: boolean,
  forceSkipMerchantReceipt?: boolean
) => {
  !forceSkipMerchantReceipt &&
    (await maybePrintMerchantReceipt(transaction))

  if (
    tenant.isTransactionReceiptsEnabled &&
    !forceSkipReceiptDrawer
  ) {
    currentTransaction.value = transaction
    showTransactionDrawer()
  } else {
    return
  }
}

const onClickCompletePayment = async () => {
  if (
    orderWithInvoice.value === null ||
    insufficientAmountConfirmationRef.value === null
  )
    return

  if (totalRemaining.value) {
    const { isCanceled } =
      await insufficientAmountConfirmationRef.value.reveal({
        amountRemaining: totalRemaining.value,
      })

    if (isCanceled) return
  }

  startLoading()
  try {
    const { invoice } = await forcePayInvoice(
      orderWithInvoice.value.invoice.id
    )
    emit("success", invoice)
  } catch {
    toast.error("Something went wrong")
  } finally {
    stopLoading()
  }
}

const paymentRetryDrawerRef = useTemplateRef(
  "paymentRetryDrawerRef"
)
const syncTransaction = async (
  ...arguments_: Parameters<typeof createTransaction>
): ReturnType<typeof createTransaction> => {
  startLoading()
  try {
    const response = await createTransaction(...arguments_)
    stopLoading()
    return response
  } catch (error) {
    stopLoading()
    Sentry.captureException(error)
    if (paymentRetryDrawerRef.value === null) {
      Sentry.captureException(
        "Payment Retry Drawer not loaded"
      )
      throw new Error("Payment Retry Drawer not loaded")
    }

    const { data, isCanceled } =
      await paymentRetryDrawerRef.value.reveal(arguments_)

    if (isCanceled || data === undefined) {
      throw new Error("Payment sync ignored by user")
    }

    return data
  }
}

const onClickTransaction = (
  transaction: OrderDetailPayment | OrderDetailRefund
) => {
  if (
    transaction.status === "cancelled" ||
    transaction.status === "pending"
  )
    return

  currentTransaction.value = transaction
  showTransactionDrawer()
}

const maybePrintMerchantReceipt = async (
  transaction: CreateTransactionResponse,
  forcePrint?: boolean
) => {
  if (
    (tenant.isAutomaticMerchantReceiptEnabled &&
      printingSupported.value) ||
    forcePrint
  ) {
    startLoading()
    try {
      const { receipt } = await retrieveTransactionReceipt(
        transaction.id,
        {
          isMerchant: true,
        }
      )

      await IPrinter.printStr({ str: receipt })
    } catch {
      toast.error("Failed to generate receipt")
    } finally {
      stopLoading()
    }
  }
}
</script>
